import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaQuoteRight, FaSearch, FaStar, FaIndustry, FaFilter, FaTimes } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';

const TestimonialsPage = () => {
  const [visibleElements, setVisibleElements] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);

  const sectionRefs = useRef({});

  // Filter testimonials based on category and search query
  useEffect(() => {
    let results = testimonialData;

    // Filter by category
    if (activeFilter !== 'all') {
      results = results.filter(testimonial =>
        testimonial.industry === activeFilter ||
        testimonial.category === activeFilter
      );
    }

    // Filter by search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      results = results.filter(testimonial =>
        testimonial.company.toLowerCase().includes(query) ||
        testimonial.author.toLowerCase().includes(query) ||
        testimonial.text.toLowerCase().includes(query) ||
        testimonial.industry.toLowerCase().includes(query)
      );
    }

    setFilteredTestimonials(results);
  }, [activeFilter, searchQuery]);

  // Load initial data
  useEffect(() => {
    setFilteredTestimonials(testimonialData);
  }, []);

  // Handle scroll-based animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Filter categories
  const categories = [
    { id: 'all', label: 'All Testimonials' },
    { id: 'sms', label: 'Bulk SMS' },
    { id: 'ussd', label: 'USSD Services' },
    { id: 'call-center', label: 'Call Center' },
    { id: 'software', label: 'Software Development' },
    { id: 'payment', label: 'Payment Solutions' }
  ];

  // Industries for filtering
  const industries = [
    { id: 'all', label: 'All Industries' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'education', label: 'Education' },
    { id: 'finance', label: 'Financial Services' },
    { id: 'retail', label: 'Retail' },
    { id: 'government', label: 'Government' },
    { id: 'hospitality', label: 'Hospitality' }
  ];

  return (
    <PageWrapper>
      <SEOHead
        title="Client Testimonials - Trusted ICT Company Kenya | Codewave Technologies Limited"
        description="Read what businesses in Kenya say about Codewave Technologies Limited's bulk SMS, USSD, SMS gateway, and mobile payment solutions. Trusted by healthcare, finance, retail, and government sectors across Nairobi and Kenya."
        keywords="Codewave testimonials, client reviews Kenya, bulk SMS reviews, USSD service reviews, ICT company reviews Nairobi, telecom solutions reviews Kenya, customer success stories, business messaging case studies, enterprise messaging testimonials"
        url="https://www.codewave.co.ke/testimonials"
        canonical="https://www.codewave.co.ke/testimonials"
      />
      {/* Hero Section */}
      <HeroSection
        id="testimonials-hero"
        ref={el => sectionRefs.current.hero = el}
        isVisible={visibleElements["testimonials-hero"]}
      >
        <Container>
          <HeroContent>
            <BreadcrumbNav>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <BreadcrumbCurrent>Testimonials</BreadcrumbCurrent>
            </BreadcrumbNav>

            <HeroTitle>
              Hear from <HighlightText>Our Clients</HighlightText>
            </HeroTitle>

            <HeroDescription>
              Discover what businesses across Africa are saying about our communication solutions
              and how they've transformed customer engagement and operational efficiency.
            </HeroDescription>
          </HeroContent>

          <HeroStars>
            <StarRating>
              <FaStar />
              <FaStar />
              <FaStar />
              <FaStar />
              <FaStar />
            </StarRating>
            <RatingText>Rated 4.9/5 based on 200+ reviews</RatingText>
          </HeroStars>

          <MobileFilterButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaTimes /> : <FaFilter />}
            {showFilters ? 'Close Filters' : 'Show Filters'}
          </MobileFilterButton>
        </Container>
      </HeroSection>

      {/* Testimonials Section */}
      <TestimonialSection>
        <Container>
          <ContentWrapper>
            <Sidebar show={showFilters}>
              <SidebarWidget>
                <WidgetTitle>Search</WidgetTitle>
                <SearchBox>
                  <SearchInput
                    type="text"
                    placeholder="Search testimonials..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <SearchIcon><FaSearch /></SearchIcon>
                  {searchQuery && (
                    <ClearButton onClick={() => setSearchQuery('')}>
                      <FaTimes />
                    </ClearButton>
                  )}
                </SearchBox>
              </SidebarWidget>

              <SidebarWidget>
                <WidgetTitle>Services</WidgetTitle>
                <FilterList>
                  {categories.map(category => (
                    <FilterItem
                      key={category.id}
                      isActive={activeFilter === category.id}
                      onClick={() => setActiveFilter(category.id)}
                    >
                      {category.label}
                      {activeFilter === category.id && <ActiveIndicator />}
                    </FilterItem>
                  ))}
                </FilterList>
              </SidebarWidget>


              <FeaturedClientLogos>
                <LogoTitle>Trusted By</LogoTitle>
                <LogoGrid>
                  {clientLogos.map((logo, index) => (
                    <ClientLogo key={index}>
                      <img src={logo} alt={`Client Logo ${index + 1}`} />
                    </ClientLogo>
                  ))}
                </LogoGrid>
              </FeaturedClientLogos>
            </Sidebar>

            <MainContent>
              <ResultsInfo>
                <ResultCount>
                  {filteredTestimonials.length} {filteredTestimonials.length === 1 ? 'Testimonial' : 'Testimonials'}
                </ResultCount>
                {activeFilter !== 'all' && (
                  <FilterBadge>
                    {categories.find(c => c.id === activeFilter)?.label ||
                      industries.find(i => i.id === activeFilter)?.label}
                    <ClearFilterButton onClick={() => setActiveFilter('all')}>
                      <FaTimes />
                    </ClearFilterButton>
                  </FilterBadge>
                )}
              </ResultsInfo>

              {filteredTestimonials.length > 0 ? (
                <TestimonialGrid
                  id="testimonial-grid"
                  ref={el => sectionRefs.current.grid = el}
                  isVisible={visibleElements["testimonial-grid"]}
                >
                  {filteredTestimonials.map((testimonial, index) => (
                    <TestimonialCard
                      key={testimonial.id}
                      isVisible={visibleElements["testimonial-grid"]}
                      delay={index * 0.1}
                      isFeatured={testimonial.featured}
                      category={testimonial.category}
                    >
                      <TestimonialCategory>
                        <FaIndustry />
                        <span>{testimonial.industry}</span>
                      </TestimonialCategory>

                      <QuoteWrapper>
                        <QuoteIcon><FaQuoteRight /></QuoteIcon>
                        <TestimonialText>{testimonial.text}</TestimonialText>
                      </QuoteWrapper>

                      <TestimonialMeta>
                        <ClientImage src={testimonial.image} alt={testimonial.author} />
                        <ClientInfo>
                          <ClientName>{testimonial.author}</ClientName>
                          <ClientTitle>{testimonial.title}, {testimonial.company}</ClientTitle>
                          <ClientStars>
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <StarIcon key={i} />
                            ))}
                          </ClientStars>
                        </ClientInfo>
                      </TestimonialMeta>
                    </TestimonialCard>
                  ))}
                </TestimonialGrid>
              ) : (
                <NoResults>
                  <NoResultsIcon><FaSearch /></NoResultsIcon>
                  <NoResultsTitle>No testimonials found</NoResultsTitle>
                  <NoResultsText>
                    Try adjusting your filters or search criteria.
                  </NoResultsText>
                  <ResetButton onClick={() => {
                    setActiveFilter('all');
                    setSearchQuery('');
                  }}>
                    Reset Filters
                  </ResetButton>
                </NoResults>
              )}
            </MainContent>
          </ContentWrapper>
        </Container>
      </TestimonialSection>

      {/* Video Testimonials Section */}
      <VideoSection
        id="video-testimonials"
        ref={el => sectionRefs.current.video = el}
        isVisible={visibleElements["video-testimonials"]}
      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>VIDEO TESTIMONIALS</SectionSubtitle>
            <SectionTitle>Watch Our Clients Share Their Experience</SectionTitle>
          </SectionHeader>

          <VideoGrid>
            {videoTestimonials.map((video, index) => (
              <VideoCard
                key={index}
                isVisible={visibleElements["video-testimonials"]}
                delay={index * 0.2}
              >
                <VideoThumbnail>
                  <img src={video.thumbnail} alt={video.title} />
                  <PlayButton href={video.link} target="_blank" rel="noopener noreferrer">
                    <PlayIcon />
                  </PlayButton>
                </VideoThumbnail>
                <VideoInfo>
                  <VideoTitle>{video.title}</VideoTitle>
                  <VideoMeta>
                    <VideoCompany>{video.company}</VideoCompany>
                    <VideoDuration>{video.duration}</VideoDuration>
                  </VideoMeta>
                </VideoInfo>
              </VideoCard>
            ))}
          </VideoGrid>
        </Container>
      </VideoSection>

      {/* Case Studies Section */}
      <CaseStudiesSection
        id="case-studies"
        ref={el => sectionRefs.current.caseStudies = el}
        isVisible={visibleElements["case-studies"]}
      >
        <Container>
          <SectionHeader>
            <SectionSubtitle>SUCCESS STORIES</SectionSubtitle>
            <SectionTitle>Featured Case Studies</SectionTitle>
            <SectionDescription>
              Explore in-depth case studies of how our solutions have helped businesses overcome challenges and achieve their goals.
            </SectionDescription>
          </SectionHeader>

          <CaseStudiesGrid>
            {caseStudies.map((study, index) => (
              <CaseStudyCard
                key={index}
                isVisible={visibleElements["case-studies"]}
                delay={index * 0.2}
              >
                <CaseStudyImage src={study.image} alt={study.title} />
                <CaseStudyContent>
                  <CaseStudyCategory>{study.category}</CaseStudyCategory>
                  <CaseStudyTitle>{study.title}</CaseStudyTitle>
                  <CaseStudyDescription>{study.description}</CaseStudyDescription>
                  <CaseStudyLink href={`/case-studies/${study.slug}`}>
                    Read Case Study
                  </CaseStudyLink>
                </CaseStudyContent>
              </CaseStudyCard>
            ))}
          </CaseStudiesGrid>

          <ViewMoreButtonWrapper>
            <ViewMoreButton href="/case-studies">View All Case Studies</ViewMoreButton>
          </ViewMoreButtonWrapper>
        </Container>
      </CaseStudiesSection>

      {/* CTA Section */}
      <CTASection
        id="testimonials-cta"
        ref={el => sectionRefs.current.cta = el}
        isVisible={visibleElements["testimonials-cta"]}
      >
        <Container>
          <CTAContent>
            <CTATitle>Ready to join our success stories?</CTATitle>
            <CTADescription>
              Contact us today to discover how our solutions can transform your business communication.
            </CTADescription>
            <CTAButton href="/contact">Get Started</CTAButton>
          </CTAContent>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

// Testimonial Data
const testimonialData = [
  {
    id: 1,
    author: "Sarah Kimani",
    title: "Marketing Director",
    company: "HealthPlus Clinics",
    image: "https://randomuser.me/api/portraits/women/45.jpg",
    text: "Codewave's bulk SMS service has transformed our patient communication. We've seen a 40% increase in appointment attendance since implementing their reminder system. The ease of scheduling messages and the detailed delivery reports have been invaluable for our team.",
    rating: 5,
    industry: "healthcare",
    category: "sms",
    featured: true
  },
  {
    id: 2,
    author: "James Omondi",
    title: "Customer Success Manager",
    company: "RetailPro Kenya",
    image: "https://randomuser.me/api/portraits/men/32.jpg",
    text: "The analytics and reporting features have been invaluable for tracking our campaign performance. We can now make data-driven decisions about our messaging strategy. Their team's responsiveness to our questions has made the entire experience seamless.",
    rating: 5,
    industry: "retail",
    category: "sms",
    featured: false
  },
  {
    id: 3,
    author: "Grace Mwangi",
    title: "IT Director",
    company: "Savanna Financial Services",
    image: "https://randomuser.me/api/portraits/women/68.jpg",
    text: "Integration with our existing CRM was seamless, and the support team was incredibly helpful throughout the process. We've been able to automate our customer communication workflow, saving hours of manual work each week. Would highly recommend!",
    rating: 5,
    industry: "finance",
    category: "sms",
    featured: false
  },
  {
    id: 4,
    author: "David Ndegwa",
    title: "Operations Manager",
    company: "Nairobi City Hospital",
    image: "https://randomuser.me/api/portraits/men/76.jpg",
    text: "The USSD service has been a game-changer for our patient registration system. Many of our patients don't have smartphones, so this solution has greatly improved accessibility. The implementation was smooth and the team provided excellent training for our staff.",
    rating: 5,
    industry: "healthcare",
    category: "ussd",
    featured: true
  },
  {
    id: 5,
    author: "Amina Hassan",
    title: "Chief Digital Officer",
    company: "Equity Bank East Africa",
    image: "https://randomuser.me/api/portraits/women/33.jpg",
    text: "We've been using Codewave's USSD platform for banking services across rural areas, and the results have exceeded our expectations. Transaction volume increased by 60% in the first quarter after implementation, and customer satisfaction scores have improved significantly.",
    rating: 5,
    industry: "finance",
    category: "ussd",
    featured: true
  },
  {
    id: 6,
    author: "John Kamau",
    title: "Head of Customer Experience",
    company: "SunPower Energy",
    image: "https://randomuser.me/api/portraits/men/22.jpg",
    text: "The call center solution has completely transformed our customer service operation. We've reduced wait times by 45% and improved first-call resolution rates. The analytics dashboard gives us valuable insights into performance metrics that help us continually improve.",
    rating: 4,
    industry: "energy",
    category: "call-center",
    featured: false
  },
  {
    id: 7,
    author: "Elizabeth Ochieng",
    title: "CEO",
    company: "Elimu Learning Centers",
    image: "https://randomuser.me/api/portraits/women/23.jpg",
    text: "Working with Codewave to develop our student management system was an excellent decision. They understood our unique requirements and delivered a solution that has streamlined our administrative processes. The software is intuitive, reliable, and has scaled with our growth.",
    rating: 5,
    industry: "education",
    category: "software",
    featured: false
  },
  {
    id: 8,
    author: "Michael Waweru",
    title: "Finance Director",
    company: "Kilimo Fresh Produce",
    image: "https://randomuser.me/api/portraits/men/52.jpg",
    text: "Implementing Codewave's payment solution has dramatically reduced our payment processing time and improved accuracy. The integration with mobile money platforms was essential for our farmer network, and the system has been extremely reliable even in areas with connectivity challenges.",
    rating: 5,
    industry: "agriculture",
    category: "payment",
    featured: false
  },
  {
    id: 9,
    author: "Patricia Njeri",
    title: "Director of Communications",
    company: "Ministry of Education",
    image: "https://randomuser.me/api/portraits/women/89.jpg",
    text: "The bulk SMS service has been crucial for our nationwide education initiatives. We've been able to reach millions of parents and teachers with important updates during the pandemic. The scalability and reliability of the system, even during peak sending times, has been impressive.",
    rating: 5,
    industry: "government",
    category: "sms",
    featured: false
  },
  {
    id: 10,
    author: "Daniel Mutua",
    title: "Head of Sales",
    company: "Safari Telecom",
    image: "https://randomuser.me/api/portraits/men/41.jpg",
    text: "Codewave's custom software development team built us a dealer management system that perfectly matched our unique business model. The attention to detail and commitment to understanding our processes resulted in a solution that has increased our distribution efficiency by 35%.",
    rating: 4,
    industry: "telecommunications",
    category: "software",
    featured: false
  },
  {
    id: 11,
    author: "Faith Wambui",
    title: "General Manager",
    company: "Serene Hotels Group",
    image: "https://randomuser.me/api/portraits/women/55.jpg",
    text: "The call center solution has improved our guest satisfaction scores significantly. The ability to route calls to the appropriate department and the detailed call logs have helped us identify training opportunities for our staff. Implementation was quick and the support has been excellent.",
    rating: 5,
    industry: "hospitality",
    category: "call-center",
    featured: true
  },
  {
    id: 12,
    author: "Robert Karanja",
    title: "Product Manager",
    company: "MobiPay Solutions",
    image: "https://randomuser.me/api/portraits/men/67.jpg",
    text: "We integrated Codewave's payment API into our mobile application, and the process was remarkably smooth. The documentation was comprehensive, and their technical team was always available to assist with any questions. Our transaction success rate increased by 28% after switching.",
    rating: 5,
    industry: "finance",
    category: "payment",
    featured: false
  },
];

// Video Testimonials
const videoTestimonials = [
  {
    title: "How HealthPlus Improved Patient Communication",
    company: "HealthPlus Clinics",
    thumbnail: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    duration: "4:35",
    link: "https://www.youtube.com/watch?v=example1"
  },
  {
    title: "Savanna Bank's Digital Transformation Journey",
    company: "Savanna Financial Services",
    thumbnail: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    duration: "6:12",
    link: "https://www.youtube.com/watch?v=example2"
  },
  {
    title: "Ministry of Education: Reaching Every School",
    company: "Ministry of Education",
    thumbnail: "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    duration: "5:48",
    link: "https://www.youtube.com/watch?v=example3"
  }
];

// Case Studies
const caseStudies = [
  {
    title: "How Equity Bank Expanded Rural Banking Access",
    description: "Learn how Equity Bank used USSD technology to provide banking services to underserved rural communities, resulting in 2.5 million new customers in 18 months.",
    category: "USSD Services",
    image: "https://images.unsplash.com/photo-1601597111158-2fceff292cdc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "equity-bank-rural-banking"
  },
  {
    title: "Nairobi City Hospital's Patient Engagement Revolution",
    description: "Discover how an integrated communication strategy reduced missed appointments by 75% and improved patient satisfaction scores by 40%.",
    category: "Bulk SMS",
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "nairobi-hospital-engagement"
  },
  {
    title: "Ministry of Education's Nationwide Emergency Alert System",
    description: "How we helped deploy a communication system reaching 8 million parents across 22,000 schools during the COVID-19 pandemic.",
    category: "SMS & USSD Integration",
    image: "https://images.unsplash.com/photo-1613896527026-f195d5c818ed?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    slug: "ministry-education-alert-system"
  }
];

// Client Logos
const clientLogos = [
  "https://via.placeholder.com/150x70?text=Client+1",
  "https://via.placeholder.com/150x70?text=Client+2",
  "https://via.placeholder.com/150x70?text=Client+3",
  "https://via.placeholder.com/150x70?text=Client+4",
  "https://via.placeholder.com/150x70?text=Client+5",
  "https://via.placeholder.com/150x70?text=Client+6",
];

export default TestimonialsPage;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// === Styled Components ===
const PageWrapper = styled.div`
  background-color: white;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

// Hero Section
const HeroSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%);
  position: relative;
  
  opacity: ${props => props.isVisible ? 1 : 0};
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const BreadcrumbNav = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  font-size: 14px;
`;

const BreadcrumbLink = styled.a`
  color: #666;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #2090B0;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 10px;
  color: #ccc;
`;

const BreadcrumbCurrent = styled.span`
  color: #2090B0;
  font-weight: 500;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: #105090;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #666;
  max-width: 700px;
  margin: 0 auto 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const HeroStars = styled.div`
  text-align: center;
  margin-top: 20px;
`;

const StarRating = styled.div`
  display: inline-flex;
  color: #f8c40c;
  font-size: 24px;
  margin-bottom: 10px;
  
  svg {
    margin: 0 2px;
  }
`;

const RatingText = styled.p`
  font-size: 15px;
  color: #666;
  font-weight: 500;
`;

const MobileFilterButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
  background: #105090;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  display: none;
  align-items: center;
  gap: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 992px) {
    display: flex;
  }
`;

// Testimonial Section
const TestimonialSection = styled.section`
  padding: 80px 0;
  background-color: white;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 30px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.aside`
  width: 280px;
  
  @media (max-width: 992px) {
    width: 100%;
    display: ${props => props.show ? 'block' : 'none'};
    margin-bottom: 30px;
  }
`;

const MainContent = styled.div`
  flex: 1;
`;

const SidebarWidget = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  margin-bottom: 25px;
`;

const WidgetTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
`;

// Search Box
const SearchBox = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  padding-right: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2090B0;
    box-shadow: 0 0 0 3px rgba(32, 144, 176, 0.1);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  color: #999;
`;

const ClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  color: #999;
  cursor: pointer;
  
  &:hover {
    color: #2090B0;
  }
`;

// Filter Lists
const FilterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FilterItem = styled.li`
  position: relative;
  padding: 10px 0;
  color: ${props => props.isActive ? '#2090B0' : '#555'};
  font-weight: ${props => props.isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding-left: ${props => props.isActive ? '15px' : '0'};
  
  &:hover {
    color: #2090B0;
  }
`;

const ActiveIndicator = styled.span`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
  background-color: #2090B0;
  border-radius: 3px;
`;

// Client Logos
const FeaturedClientLogos = styled.div`
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
`;

const LogoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #105090;
  margin: 0 0 20px;
  text-align: center;
`;

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
`;

const ClientLogo = styled.div`
  background: #f8f9fb;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  img {
    max-width: 100%;
    max-height: 40px;
    object-fit: contain;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }
`;

// Results Info
const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;
`;

const ResultCount = styled.div`
  font-size: 15px;
  color: #666;
`;

const FilterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: #f0f0f0;
  border-radius: 30px;
  padding: 5px 15px;
  font-size: 14px;
  color: #555;
`;

const ClearFilterButton = styled.button`
  background: none;
  border: none;
  margin-left: 8px;
  padding: 0;
  color: #999;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    color: #2090B0;
  }
`;

// Testimonial Grid
const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  
  opacity: ${props => props.isVisible ? 1 : 0.4};
  transition: opacity 0.6s ease;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border-top: ${props => props.isFeatured ? '4px solid ' + getCategoryColor(props.category) : '1px solid #f0f0f0'};
  grid-column: ${props => props.isFeatured ? '1 / -1' : 'auto'};
  transition: all 0.3s ease;
  position: relative;
  
  opacity: 0;
  animation: ${fadeIn} 0.6s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    grid-column: 1;
  }
`;

// Helper function to get category color
const getCategoryColor = (category) => {
  const colors = {
    'sms': '#2090B0',
    'ussd': '#105090',
    'call-center': '#6A4C93',
    'software': '#1A936F',
    'payment': '#C88A3D'
  };
  return colors[category] || '#2090B0';
};

const TestimonialCategory = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #777;
  font-size: 14px;
  margin-bottom: 20px;
  
  svg {
    color: #999;
  }
`;

const QuoteWrapper = styled.div`
  position: relative;
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: -5px;
  right: 0;
  color: #f3f3f3;
  font-size: 24px;
`;

const TestimonialText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #000;
  margin-bottom: 30px;
  font-style: italic;
  min-height: 130px;
`;

const TestimonialMeta = styled.div`
  display: flex;
  align-items: center;
`;

const ClientImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 15px;
`;

const ClientInfo = styled.div``;

const ClientName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px;
  color: #105090;
`;

const ClientTitle = styled.p`
  font-size: 14px;
  color: #777;
  margin: 0 0 8px;
`;

const ClientStars = styled.div`
  display: flex;
  color: #f8c40c;
`;

const StarIcon = styled(FaStar)`
  font-size: 14px;
  margin-right: 2px;
`;

// No Results
const NoResults = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;

const NoResultsIcon = styled.div`
  font-size: 40px;
  color: #ddd;
  margin-bottom: 20px;
`;

const NoResultsTitle = styled.h3`
  font-size: 22px;
  color: #105090;
  margin-bottom: 10px;
`;

const NoResultsText = styled.p`
  font-size: 16px;
  color: #777;
  margin-bottom: 25px;
`;

const ResetButton = styled.button`
  background: linear-gradient(90deg, #105090, #2090B0);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 12px 25px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

// Video Section
const VideoSection = styled.section`
  padding: 100px 0;
  background: #f8f9fb;
  
  opacity: ${props => props.isVisible ? 1 : 0.4};
  transition: opacity 0.6s ease, transform 0.6s ease;
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 50px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #2090B0;
  margin-bottom: 15px;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: #105090;
  margin-bottom: 20px;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
  
  @media (max-width: 576px) {
    font-size: 26px;
  }
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const VideoThumbnail = styled.div`
  position: relative;
  height: 180px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 1;
  }
`;

const PlayButton = styled.a`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: translate(-50%, -50%) scale(1.1);
  }
  
  &:before {
    content: '';
    position: absolute;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    animation: ${pulse} 2s infinite;
  }
`;

const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 10px 0 10px 18px;
  border-color: transparent transparent transparent #2090B0;
  margin-left: 4px;
`;

const VideoInfo = styled.div`
  padding: 20px;
`;

const VideoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #105090;
  line-height: 1.4;
`;

const VideoMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const VideoCompany = styled.span`
  font-size: 14px;
  color: #777;
`;

const VideoDuration = styled.span`
  font-size: 14px;
  color: #999;
`;

// Case Studies Section
const CaseStudiesSection = styled.section`
  padding: 100px 0;
  background: white;
  
  opacity: ${props => props.isVisible ? 1 : 0.4};
  transition: opacity 0.6s ease, transform 0.6s ease;
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const CaseStudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  opacity: 0;
  animation: ${fadeInUp} 0.8s ease forwards;
  animation-delay: ${props => props.delay}s;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
`;

const CaseStudyImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CaseStudyContent = styled.div`
  padding: 25px;
`;

const CaseStudyCategory = styled.span`
  display: inline-block;
  background: #f0f0f0;
  border-radius: 30px;
  padding: 5px 12px;
  font-size: 12px;
  color: #555;
  margin-bottom: 15px;
`;

const CaseStudyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 15px;
  color: #105090;
  line-height: 1.4;
`;

const CaseStudyDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 20px;
`;

const CaseStudyLink = styled.a`
  color: #2090B0;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  position: relative;
  padding-bottom: 3px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: #2090B0;
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const ViewMoreButtonWrapper = styled.div`
  text-align: center;
  margin-top: 50px;
`;

const ViewMoreButton = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 35px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-position: right center;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

// CTA Section
const CTASection = styled.section`
  padding: 100px 0;
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  animation: ${gradientAnimation} 10s infinite;
  color: white;
  
  opacity: ${props => props.isVisible ? 1 : 0.4};
  transition: opacity 0.6s ease, transform 0.6s ease;
  transform: ${props => props.isVisible ? 'translateY(0)' : 'translateY(20px)'};
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const CTAContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 30px;
  }
  
  @media (max-width: 576px) {
    font-size: 26px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: white;
  color: #105090;
  font-size: 16px;
  font-weight: 600;
  padding: 15px 35px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;